import { useMachine } from '@xstate/react'
import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Checkbox } from 'sutra/components/Checkbox/Checkbox'

import { Loader } from 'components'
import { EditProject, Selector } from 'features/project-form/components'
import {
  ModalHeader,
  ModalWrapper,
} from 'features/project-form/components/modal'
import { ValidationProgress } from 'features/project-form/components/validation-progress'
import { CsvForm } from 'features/project-form/features/csv/CsvForm'
import { ProductForm } from 'features/project-form/features/product/ProductForm'
import { SourceForm } from 'features/project-form/features/source'
import {
  ProductForSource,
  ProjectSourceFile,
  ProjectSourceURL,
} from 'features/project-form/features/source/types'
import {
  ProjectActions,
  projectMachine,
  ProjectStates,
} from 'features/project-form/machine'
import { useProjectFormService } from 'features/project-form/service'
import { validateProjectSourcesWithProgress } from 'features/project-form/service/validate-with-progress'
import {
  FormAdditionalValues,
  ValidatePhaseEvent,
} from 'features/project-form/types'
import { transformProducts } from 'features/project-form/utils'
import { projectEditValidationSchema as validationSchema } from 'features/project-form/validators'
import { useProjectService } from 'features/project/service'

type Props = { handleClose: () => void; projectId: string }

const initialValues: FormAdditionalValues = {
  products: [],
  sources: [],
  bypassValidations: false,
}

export const ProjectEditForm: React.FC<Props> = ({
  handleClose,
  projectId,
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [validatePhase, setValidatePhase] = useState<ValidatePhaseEvent | null>(
    null,
  )
  const projectService = useProjectService()
  const projectFormService = useProjectFormService()

  const { data: details } = useQuery(
    ['project', projectId],
    () => projectService.getProject(projectId),
    {
      refetchOnMount: false,
      enabled: !!projectId,
    },
  )
  const [sourceToEditIndex, setSourceToEditIndex] = useState<null | number>(
    null,
  )
  const [productToEditIndex, setProductToEditIndex] = useState<null | number>(
    null,
  )
  const [current, send] = useMachine(projectMachine)
  const {
    data: existingProducts,
    isLoading,
    refetch,
  } = useQuery(
    ['products-list'],
    () => projectFormService.getProjectProducts(projectId),
    {
      enabled: false,
    },
  )
  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async ({ sources, products }, { setFieldError }) => {
      if (sources.some((source) => !source.product_id)) {
        setFieldError('sources', 'Every source must be assigned to a product.')
        return
      }

      try {
        setLoading(true)

        const allProducts = await handleInputsForUpload(sources, products)
        formik.setFieldValue('sources', [...sources])

        if (!formik.values.bypassValidations) {
          setValidatePhase(null)
          try {
            await validateProjectSourcesWithProgress({
              service: projectFormService,
              payload: {
                sources,
                products: allProducts,
                project_uuid: projectId,
                project_title: details?.title ?? '',
              },
              onPhase: setValidatePhase,
            })
          } finally {
            setValidatePhase(null)
          }
          formik.setFieldValue('sources', [...formik.values.sources])
          toast.success('Validations passed. Uploading...')
        }

        await projectFormService.setProjectSources({
          sources,
          stop_words: [],
          source_addition: true,
          proj_uuid: projectId,
          products: allProducts,
          project_title: details?.title ?? '',
        })
        setLoading(false)
        toast.success('Sources were successfully added')
        handleClose()
      } catch (e: unknown) {
        setLoading(false)
        const err = e as {
          response?: { data?: { detail?: string } }
          message?: string
        }
        toast.error(
          err?.response?.data?.detail ?? err?.message ?? 'An Error Occurred',
        )
      }
    },
  })

  const handleInputsForUpload = async (
    sources: Array<ProjectSourceFile | ProjectSourceURL>,
    products: ProductForSource[],
  ): Promise<Array<{ name: string; id: string | number }>> => {
    const existingProductsPayload = (existingProducts || [])
      .filter((product) =>
        sources.find((source) => source.product_id === product.uuid),
      )
      .map((product) => ({ name: product.name, id: product.uuid }))

    const fileSources = sources.filter(
      (source): source is ProjectSourceFile =>
        'file' in source && !!source.file,
    )

    // s3 paths are returned from upload endpoint
    const paths = await Promise.all(
      fileSources.map(({ file }) => projectFormService.uploadFile(file)),
    )

    // we need to replace the key with the returned path
    fileSources.forEach((source, index) => {
      source.key = paths[index]!.data.path
    })

    return [...products, ...existingProductsPayload]
  }

  const bypassValidations = (checked: boolean): void => {
    formik.setFieldValue('bypassValidations', checked)
  }

  const submit = (): void => {
    send(ProjectActions.SUBMIT)
  }

  const handleValidateOnly = async (): Promise<void> => {
    setLoading(true)
    const allProducts = await handleInputsForUpload(
      formik.values.sources,
      formik.values.products,
    )
    formik.setFieldValue('sources', [...formik.values.sources])
    setValidatePhase(null)
    try {
      await validateProjectSourcesWithProgress({
        service: projectFormService,
        payload: {
          sources: formik.values.sources,
          products: allProducts,
          project_uuid: projectId,
          project_title: details?.title ?? '',
        },
        onPhase: setValidatePhase,
      })
      toast.success('Validations passed.')
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { detail?: string } }
        message?: string
      }
      toast.error(
        err?.response?.data?.detail ?? err?.message ?? 'An Error Occurred',
      )
    } finally {
      setValidatePhase(null)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (current.matches(ProjectStates.FORM) && !existingProducts) {
      refetch()
    }
  }, [current.value, current, existingProducts, refetch])

  return (
    <ModalWrapper>
      <ModalHeader
        handleClose={handleClose}
        handleBack={
          current.context.backButton
            ? () => {
                send(ProjectActions.BACK)
              }
            : undefined
        }
      />
      {current.matches(ProjectStates.SELECTOR) && (
        <Selector
          onBlankClick={() => send(ProjectStates.FORM)}
          onCSVClick={() => send(ProjectStates.UPLOAD)}
        />
      )}
      {current.matches(ProjectStates.UPLOAD) && (
        <CsvForm
          projectId={projectId}
          onSuccess={({ sources, products }) => {
            send(ProjectStates.FORM)
            formik.setFieldValue('sources', sources)
            formik.setFieldValue('products', products)
          }}
        />
      )}
      {current.matches(ProjectStates.FORM) && (
        <EditProject
          isLoading={isLoading}
          title={details?.title}
          formik={formik}
          handleOpenProduct={() => send(ProjectStates.PRODUCT)}
          handleOpenSource={() => send(ProjectStates.SOURCE)}
          setSourceToEditIndex={setSourceToEditIndex}
          setProductToEditIndex={setProductToEditIndex}
        />
      )}
      {current.matches(ProjectStates.SOURCE) && (
        <SourceForm
          products={[
            ...formik.values.products,
            ...transformProducts(existingProducts || []),
          ]}
          reservedKeywords={[]}
          sourceNames={formik.values.sources.map((source) => source.title)}
          handleOpenProject={submit}
          addSource={(source) => {
            if (sourceToEditIndex !== null) {
              formik.values.sources[sourceToEditIndex] = source
              formik.setFieldValue('sources', formik.values.sources)
              setSourceToEditIndex(null)
            } else {
              formik.setFieldValue('sources', [
                ...formik.values.sources,
                source,
              ])
            }
          }}
          sourceToEdit={
            sourceToEditIndex !== null
              ? (formik.values.sources[sourceToEditIndex] ?? null)
              : null
          }
          sourceToEditIndex={sourceToEditIndex}
          setSourceToEditIndex={setSourceToEditIndex}
          deleteSource={() => {
            if (sourceToEditIndex !== null) {
              submit()
              formik.setFieldValue(
                'sources',
                formik.values.sources.filter((_, i) => sourceToEditIndex !== i),
              )
              setSourceToEditIndex(null)
            }
          }}
        />
      )}
      {current.matches(ProjectStates.PRODUCT) && (
        <ProductForm
          addProduct={(product) => {
            if (productToEditIndex !== null) {
              formik.values.products[productToEditIndex] = product
              formik.setFieldValue('products', formik.values.products)
              setProductToEditIndex(null)
            } else {
              formik.setFieldValue('products', [
                ...formik.values.products,
                product,
              ])
            }
          }}
          productToEdit={
            productToEditIndex !== null
              ? (formik.values.products[productToEditIndex] ?? null)
              : null
          }
          handleOpenProject={submit}
          deleteProduct={() => {
            if (productToEditIndex !== null) {
              submit()
              formik.setFieldValue(
                'products',
                formik.values.products.filter(
                  (_, i) => productToEditIndex !== i,
                ),
              )
              setProductToEditIndex(null)
            }
          }}
          setProductToEditIndex={setProductToEditIndex}
        />
      )}
      <ValidateOnlyButton
        variant="primary"
        type="submit"
        onClick={handleValidateOnly}
        disabled={!formik.isValid || formik.isSubmitting}
      >
        Validate Only
      </ValidateOnlyButton>
      <Checkbox onChange={bypassValidations}>Bypass validations</Checkbox>
      {loading && (
        <Overlay>
          {validatePhase !== null ? (
            <ValidationProgress event={validatePhase} />
          ) : (
            <Loader />
          )}
        </Overlay>
      )}
    </ModalWrapper>
  )
}

const Overlay = styled.div`
  background: rgba(255, 255, 255, 0.7);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ValidateOnlyButton = styled(Button)`
  margin-right: 12px;
`
